import { CourseRequirementType, ICourseRequirement, ICreateCourseRequirement } from '@/types/courses';
import { defaultNewRequirement, defaultSettings } from '../utils/course-utils';
import { TranslateString } from '@/types/translation.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import Modal from '@ui/modal/modal';
import { BrandliveEvent } from '@/types/projects.types';
import Checkbox from '@ui/checkbox/checkbox';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Text from '@ui/text/text/text';
import Popover from '@ui/popover/popover';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import { numClamp } from '@/utils/num-clamp';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { useRef } from 'react';
import SessionSelector from './controls/session-selector';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

type Props = {
  showAddNewRequirementModal: boolean;
  closeAddNewRequirementModal: () => void;
  canSaveNewRequirement: boolean;
  savingRequirement: boolean;
  isEditorRole: boolean;
  requirementName: string;
  newRequirement: ICreateCourseRequirement;
  setNewRequirement: (requirement: ICreateCourseRequirement) => void;
  requirementToUpdate: string | null;
  updateRequirement: () => void;
  createRequirement: () => void;
  workingEvent: BrandliveEvent | null;
  getPulseCheckDurationDisplay: (duration: number) => string;
  requirements: ICourseRequirement[] | undefined;
};
export default function CourseAddRequirementModal({
  showAddNewRequirementModal,
  closeAddNewRequirementModal,
  canSaveNewRequirement,
  savingRequirement,
  isEditorRole,
  requirementName,
  newRequirement,
  setNewRequirement,
  requirementToUpdate,
  updateRequirement,
  createRequirement,
  workingEvent,
  getPulseCheckDurationDisplay,
  requirements,
}: Props) {
  const popoverPortalRef = useRef<HTMLDivElement | null>(null);

  return (
    <Modal
      backgroundColor="var(--gray-900)"
      contentWrapperClassName="add-requirement-modal padding-v-24 padding-h-40 jc-space-between"
      open={showAddNewRequirementModal}
      onRequestClose={closeAddNewRequirementModal}
      customSize={{
        width: '720px',
        height: newRequirement.type === CourseRequirementType.watched_minute ? '328px' : '458',
        minHeight: '300px',
        maxHeight: 'calc(100vh - 80px)',
        maxWidth: 'calc(100vw - 80px)',
      }}
      renderHeaderContent={() => {
        return (
          <div className="flex">
            <Container className="flex-1">
              <Text tag="h3" className="H3 margin-b-4">
                {requirementName}
              </Text>
              <Text className="P1" color="var(--gray-300)">
                {newRequirement.type === CourseRequirementType.watched_minute
                  ? `Watch ${newRequirement.required_amount ?? 'x'} minute${newRequirement.required_amount === 1 ? '' : 's'} to complete this requirement`
                  : `Demonstrate your engagement via ${newRequirement.required_amount ?? 'x'} check-in${newRequirement.required_amount === 1 ? '' : 's'}`}
              </Text>
            </Container>
            <Popover
              closeOnClick
              containerClassName="margin-r-40 margin-t-24"
              backgroundColor="var(--gray-700)"
              contentClassName="margin-t-4"
              forcePosition="bottom-left"
              icon={ICONS.KEYBOARD_ARROW_DOWN}
              renderTriggerButton={({ open, closing }) => {
                return (
                  <Button
                    backgroundColor="var(--gray-700)"
                    height="32px"
                    width="140px"
                    className="padding-t-10 flex ai-center jc-center gap-10"
                  >
                    <Text className="S3">{requirementName}</Text>
                    <RotatableIcon
                      name={ICONS.KEYBOARD_ARROW_DOWN}
                      color="var(--gray-400)"
                      size={16}
                      rotate={open && !closing ? '180deg' : '0'}
                    />
                  </Button>
                );
              }}
            >
              <Button
                className="radius-0"
                hoverBackgroundColor="var(--gray-500)"
                width="100%"
                onClick={() =>
                  setNewRequirement({
                    ...defaultNewRequirement,
                    type: CourseRequirementType.watched_minute,
                    settings: defaultSettings(CourseRequirementType.watched_minute),
                  })
                }
                data-testid="course-edit-requirement-type-watchtime-btn"
              >
                <Text className="S3">Watchtime</Text>
              </Button>
              <Button
                className="radius-0"
                hoverBackgroundColor="var(--gray-500)"
                width="100%"
                onClick={() =>
                  setNewRequirement({
                    ...defaultNewRequirement,
                    type: CourseRequirementType.pulse_check,
                    settings: defaultSettings(CourseRequirementType.pulse_check),
                  })
                }
                data-testid="course-edit-requirement-type-pulse-check-btn"
              >
                <Text className="S3">Pulse check</Text>
              </Button>
            </Popover>
          </div>
        );
      }}
      renderFooterContent={() => {
        return (
          <Container className="flex fd-row jc-flex-end ai-center gap-16">
            <Button
              onClick={closeAddNewRequirementModal}
              disabled={savingRequirement}
              design="LocalBttnSec"
              height="53px"
              width="95px"
              className="L1"
            >
              Cancel
            </Button>

            <Button
              padding="16px 24px"
              width="auto"
              disabled={!canSaveNewRequirement}
              onClick={requirementToUpdate ? updateRequirement : createRequirement}
              loading={savingRequirement}
              backgroundColor={getThemeColorCSSVariable()}
            >
              <Text
                className="L1"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                Save
              </Text>
            </Button>
          </Container>
        );
      }}
    >
      <div className="flex-column gap-10 padding-t-36">
        <Container background="var(--gray-800)" padding="16px" borderRadius="12px">
          <OptionalComponent display={newRequirement.type === CourseRequirementType.watched_minute}>
            <div className="flex jc-space-between">
              <div className="flex-column">
                <Text className="L3" color="var(--gray-400)">
                  SESSION TO WATCH
                </Text>
                <SessionSelector
                  selectedSession={newRequirement.session_id}
                  onChange={(sessionId) => setNewRequirement({ ...newRequirement, session_id: sessionId })}
                  workingEvent={workingEvent}
                  requirements={requirements}
                  selectedType={newRequirement.type}
                />
              </div>
              <div className="flex-column">
                <Text className="L3 margin-r-100" color="var(--gray-400)">
                  MINUTES
                </Text>
                <InputWithOverlay
                  wrapperClass="padding-t-10 width-164"
                  border="solid 1px transparent"
                  hoverBackground="var(--gray-900)"
                  hoverBorder="solid 1px var(--gray-300)"
                  background="var(--gray-800)"
                  inputHeight="32px"
                  inputProps={{
                    value: newRequirement.required_amount,
                    pattern: '[0-9]*',
                    className: 'radius-4 padding-10 H4',
                    onChange: (e) => {
                      const minutes = Number(e.target.value);
                      setNewRequirement({
                        ...newRequirement,
                        required_amount: numClamp(minutes, 0, 1000),
                      });
                    },
                  }}
                />
              </div>
            </div>
          </OptionalComponent>
          <OptionalComponent display={newRequirement.type === CourseRequirementType.pulse_check}>
            <div className="flex jc-space-between gap-10">
              <div className="flex-column flex-1 min-width-0">
                <Text className="L3 flex-1" color="var(--gray-400)">
                  SESSION TO WATCH
                </Text>
                <SessionSelector
                  selectedSession={newRequirement.session_id}
                  onChange={(sessionId) => setNewRequirement({ ...newRequirement, session_id: sessionId })}
                  workingEvent={workingEvent}
                  requirements={requirements}
                  selectedType={newRequirement.type}
                />
              </div>
              <div className="flex-column" style={{ flex: '.25' }}>
                <TooltipPortal tooltipContent={<Text>Number of total pulse checks</Text>}>
                  <Text className="L3" color="var(--gray-400)">
                    COUNT
                  </Text>
                </TooltipPortal>
                <InputWithOverlay
                  wrapperClass="padding-t-10 width-100"
                  border="solid 1px transparent"
                  hoverBackground="var(--gray-900)"
                  hoverBorder="solid 1px var(--gray-300)"
                  background="var(--gray-800)"
                  inputHeight="32px"
                  inputProps={{
                    value: newRequirement.points,
                    pattern: '[0-9]*',
                    className: 'radius-4 padding-0 H4',
                    onChange: (e) => {
                      const minutes = Number(e.target.value);
                      setNewRequirement({
                        ...newRequirement,
                        points: numClamp(minutes, 0, 1000),
                      });
                    },
                  }}
                />
              </div>
              <div className="flex-column" style={{ flex: '.25' }}>
                <TooltipPortal tooltipContent={<Text>Duration of each pulse check</Text>}>
                  <Text className="L3" color="var(--gray-400)">
                    DISPLAY
                  </Text>
                </TooltipPortal>
                <Popover
                  closeOnClick
                  maxHeight="400px"
                  forcePosition="bottom-center"
                  backgroundColor="var(--gray-800)"
                  portalEl={popoverPortalRef}
                  renderTriggerButton={() => {
                    return (
                      <Button
                        backgroundColor="var(--gray-800)"
                        width="100px"
                        className="padding-0 padding-l-8 padding-t-10 flex ai-center jc-flex-start gap-10"
                      >
                        <Text className="H4 padding-0" color="var(--gray-400)">
                          {getPulseCheckDurationDisplay(
                            (newRequirement.settings?.pulseCheckDisplayDuration as number) || 5
                          )}
                        </Text>
                        <Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} color="var(--gray-400)" />
                      </Button>
                    );
                  }}
                >
                  {({ triggerBounds }) => {
                    return (
                      <Container
                        background="var(--gray-700)"
                        className="position-fixed radius-8"
                        style={{
                          top: triggerBounds?.top ? triggerBounds?.top + 40 : 0,
                          left: triggerBounds?.left,
                          zIndex: 1200,
                          height: '300px',
                          overflow: 'scroll',
                        }}
                      >
                        {[5, 10, 20, 30, 60, 120, 600].map((duration) => {
                          return (
                            <div key={duration}>
                              <Button
                                className="radius-0"
                                hoverBackgroundColor="var(--gray-500)"
                                width="100%"
                                onClick={() =>
                                  setNewRequirement({
                                    ...newRequirement,
                                    settings: {
                                      ...newRequirement.settings,
                                      pulseCheckDisplayDuration: duration,
                                    },
                                  })
                                }
                              >
                                <Text>{duration}</Text>
                              </Button>
                            </div>
                          );
                        })}
                      </Container>
                    );
                  }}
                </Popover>
              </div>
              <div className="flex-column" style={{ flex: '.25' }}>
                <TooltipPortal tooltipContent={<Text>How many pulse checks needed to pass</Text>}>
                  <Text className="L3" color="var(--gray-400)" whiteSpace="nowrap">
                    PASS THRESHOLD
                  </Text>
                </TooltipPortal>
                <InputWithOverlay
                  wrapperClass="padding-t-10 width-100"
                  border="solid 1px transparent"
                  hoverBackground="var(--gray-900)"
                  hoverBorder="solid 1px var(--gray-300)"
                  background="var(--gray-800)"
                  inputHeight="32px"
                  inputProps={{
                    value: newRequirement.required_amount,
                    pattern: '[0-9]*',
                    className: 'radius-4 padding-0 H4',
                    onChange: (e) => {
                      const minutes = Number(e.target.value);
                      setNewRequirement({
                        ...newRequirement,
                        required_amount: numClamp(minutes, 0, 1000),
                      });
                    },
                  }}
                />
              </div>
            </div>
          </OptionalComponent>
        </Container>
        <OptionalComponent display={newRequirement.type === CourseRequirementType.pulse_check}>
          <Container background="var(--gray-800)" padding="16px" borderRadius="12px">
            <div className="flex-column gap-18">
              <div className="flex jc-space-between ai-center">
                <Text className="L3" color="var(--gray-400)" width="200px">
                  PULSE CHECK TEXT
                </Text>
                <InputWithOverlay
                  wrapperClass="min-width-100 max-width-400"
                  border="solid 1px transparent"
                  hoverBackground="var(--gray-900)"
                  hoverBorder="solid 1px var(--gray-300)"
                  background="var(--gray-800)"
                  inputHeight="20px"
                  inputProps={{
                    value: (newRequirement.settings?.pulseCheckText as TranslateString)?.base,
                    className: 'radius-4 padding-0 S2',
                    placeholder: 'Are you still watching?',
                    style: { direction: 'rtl' },
                    onChange: (e) => {
                      const base = e.target.value;
                      setNewRequirement({
                        ...newRequirement,
                        settings: {
                          ...newRequirement.settings,
                          pulseCheckText: {
                            ...((newRequirement.settings?.pulseCheckText as TranslateString) || {}),
                            base,
                          },
                        },
                      });
                    },
                  }}
                />
              </div>
              <div className="flex jc-space-between ai-center">
                <Text className="L3" color="var(--gray-400)" width="200px">
                  PULSE CHECK BUTTON TEXT
                </Text>
                <InputWithOverlay
                  wrapperClass="min-width-100 max-width-400"
                  border="solid 1px transparent"
                  hoverBackground="var(--gray-900)"
                  hoverBorder="solid 1px var(--gray-300)"
                  background="var(--gray-800)"
                  inputHeight="20px"
                  inputProps={{
                    value: (newRequirement.settings?.pulseCheckButtonText as TranslateString)?.base,
                    className: 'radius-4 padding-0 S2',
                    placeholder: 'Yes',
                    style: { direction: 'rtl' },
                    disabled: !!(requirementToUpdate && isEditorRole),
                    onChange: (e) => {
                      const base = e.target.value;
                      setNewRequirement({
                        ...newRequirement,
                        settings: {
                          ...newRequirement.settings,
                          pulseCheckText: {
                            ...((newRequirement.settings?.pulseCheckButtonText as TranslateString) || {}),
                            base,
                          },
                        },
                      });
                    },
                  }}
                />
              </div>
            </div>
          </Container>
          <div
            data-testid={`course-edit-requirement-pulse-check-audible-notification-container`}
            onClick={() =>
              setNewRequirement({
                ...newRequirement,
                settings: {
                  ...newRequirement.settings,
                  audibleNotification: !newRequirement.settings?.audibleNotification,
                },
              })
            }
          >
            <Checkbox
              label="Audible notification"
              labelClassName="S2"
              size={16}
              color="var(--gray-400)"
              checked={!!newRequirement.settings?.audibleNotification}
              value="audible-notification"
              data-testid="course-edit-requirement-pulse-check-audible-notification-checkbox"
              onChange={(_, isOn) =>
                setNewRequirement({
                  ...newRequirement,
                  settings: {
                    ...newRequirement.settings,
                    audibleNotification: isOn,
                  },
                })
              }
            />
          </div>
        </OptionalComponent>
      </div>
    </Modal>
  );
}
